/**
 * The stolen uniform (02 §6.4), said out loud. It has two conditions and the game never named
 * either: a dead soldier within reach, AND a building somebody lives or works in within three
 * metres of you. Found in play - "I don't understand how to get a uniform", and later "dragging a
 * body to a door in a bigger building won't let me swap clothes" - so the prompt now tells him
 * which half he is missing, and the buildings that count come from the sim's own data. Pure.
 */

import { FACTS } from '../../meta/content/archetypeFacts';
import { INTERIORS as SHELTERS } from '../../sim/objectives/disguise';
import { PROPS } from '../../sim/props';
import type { MissionDef, PropKind, World } from '../../sim/types';

/**
 * Where a man can be stripped, taken from the SIM's own rule (`PropSpec.shelter`) rather than kept
 * here as a second copy. The copy is how a body dragged to a barracks came to do nothing while the
 * HUD cheerfully said "DRAG HIM TO THE DOOR": two lists, and only one of them knew about barracks.
 */
const INTERIORS: ReadonlyArray<PropKind> = SHELTERS;

/** How close the body must be, and how close the building - the sim's own numbers. */
const BODY_REACH = 1.6;
const BUILDING_REACH = 3;
/** Near enough to be told what is missing, without nagging across the map. */
const HINT_RANGE = 6;

const near = (ax: number, az: number, bx: number, bz: number, m: number): boolean =>
  (ax - bx) * (ax - bx) + (az - bz) * (az - bz) <= m * m;

/** A mission where a uniform CAN be taken: it has somewhere to take one. Drives the in-mission hint only. */
export function hasUniformRoute(def: MissionDef): boolean {
  return def.props.some((p) => INTERIORS.includes(p.kind));
}

/**
 * A mission where the uniform is the way IN, not merely a thing the ground allows (`ArchetypeFacts.uniformRoute`:
 * 9, 12, 21, 22, 30). Nearly every map has a hut, so the standing order used to ride on thirty missions that
 * never wanted it - six lines of orders on mission 36, one of them noise.
 */
export function uniformIsTheRoute(def: MissionDef): boolean {
  return FACTS[def.archetype].uniformRoute && hasUniformRoute(def);
}

/** The standing order, for the missions whose briefing calls the uniform the intended way in. */
export function uniformOrders(def: MissionDef): string | null {
  if (!uniformIsTheRoute(def)) return null;
  return 'A UNIFORM IS THE WAY IN: KILL A SOLDIER AT A BUILDING, THEN INTERACT OVER HIS BODY';
}

/**
 * What to tell him right now. Null unless he is close to half of it: standing over a body away
 * from any building, or beside a building with no body to hand.
 */
export function uniformHint(def: MissionDef, world: World): string | null {
  if (world.objective.disguised || world.ranger.status !== 'active' || !hasUniformRoute(def)) return null;
  const r = world.ranger;

  const body = world.enemies.find((e) => e.state === 'dead' && near(e.x, e.z, r.x, r.z, HINT_RANGE));
  const building = def.props.find((p) => {
    if (!INTERIORS.includes(p.kind)) return false;
    // Its footprint counts: standing at a guard house's door is three metres from its WALL, not its centre.
    const shape = PROPS[p.kind].shapes[0];
    const half = Math.max(shape?.r ?? 0, shape?.hx ?? 0, shape?.hz ?? 0);
    return near(p.x, p.z, r.x, r.z, BUILDING_REACH + half + HINT_RANGE);
  });
  if (!body && !building) return null;

  // Both are about: the sim will be offering the interaction, so say nothing.
  const bodyClose = body !== undefined && near(body.x, body.z, r.x, r.z, BODY_REACH);
  if (body && building && bodyClose) return null;

  if (body && !building) return 'NO COVER TO CHANGE IN: DRAG HIM TO A BUILDING SOMEBODY LIVES OR WORKS IN';
  if (building && !body) return 'A UNIFORM NEEDS A BODY HERE: TAKE A SOLDIER AT THIS DOOR QUIETLY';
  return 'DRAG HIM TO THE DOOR TO TAKE HIS UNIFORM';
}

/**
 * True when hauling THIS body somewhere would earn a uniform: the mission has the buildings, he is
 * not already wearing one, and he is not standing at a door with it (where the swap is offered).
 * The drag prompt says why - a body in the open is the commonest way to meet this rule.
 */
export function dragEarnsUniform(def: MissionDef, world: World): boolean {
  if (world.objective.disguised || !hasUniformRoute(def)) return false;
  return uniformHint(def, world) !== null || world.ranger.dragging >= 0;
}
